/**
 * Admin Account Unlock Page
 * =========================
 * Self-service unlock for Super Admins who get locked out.
 * Supports:
 * - Email link verification
 * - OTP-based unlock and login
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Shield, 
  Mail, 
  Key, 
  Unlock, 
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminUnlock = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if this is a token verification from email link
  const tokenFromUrl = searchParams.get('token');
  const emailFromUrl = searchParams.get('email');

  const [mode, setMode] = useState(tokenFromUrl ? 'verify-token' : 'choose');
  const [email, setEmail] = useState(emailFromUrl || '');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // If we have a token from URL, verify it immediately
  useEffect(() => {
    if (tokenFromUrl && emailFromUrl) {
      verifyUnlockToken(tokenFromUrl, emailFromUrl);
    }
  }, [tokenFromUrl, emailFromUrl]);

  const verifyUnlockToken = async (token, email) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/security/super-admin/verify-unlock?token=${token}&email=${encodeURIComponent(email)}`, {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        toast({
          title: 'Account Unlocked',
          description: 'Your account has been unlocked. You can now log in.',
        });
        // Redirect to admin login after 2 seconds
        setTimeout(() => navigate('/admin/login'), 2000);
      } else {
        setError(data.detail || 'Invalid or expired unlock link');
      }
    } catch (err) {
      setError('Failed to verify unlock link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const requestUnlockLink = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/security/super-admin/request-unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Unlock Link Sent',
          description: 'Check your email for the unlock link.',
        });
        setMode('link-sent');
      } else {
        setError(data.detail || 'Failed to send unlock link');
      }
    } catch (err) {
      setError('Failed to request unlock link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const requestOtp = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/security/super-admin/send-unlock-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'OTP Sent',
          description: 'Check your email for the unlock OTP.',
        });
        setMode('enter-otp');
      } else {
        setError(data.detail || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpAndLogin = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/security/super-admin/otp-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, otp })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store token and user info in sessionStorage
        sessionStorage.setItem('token', data.access_token);
        sessionStorage.setItem('user', JSON.stringify(data.user));
        
        toast({
          title: 'Success!',
          description: 'Account unlocked and logged in successfully.',
        });
        
        // Redirect to admin dashboard
        window.location.href = '/admin';
      } else {
        setError(data.detail || 'Invalid OTP');
      }
    } catch (err) {
      setError('Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-green-600 mb-2">Account Unlocked!</h2>
            <p className="text-slate-600 mb-4">
              Your account has been unlocked successfully. Redirecting to login...
            </p>
            <Button onClick={() => navigate('/admin/login')} className="w-full">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Verifying token from email
  if (mode === 'verify-token' && loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Verifying Unlock Link</h2>
            <p className="text-slate-600">Please wait...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-purple-600" />
          </div>
          <CardTitle>Admin Account Recovery</CardTitle>
          <CardDescription>
            {mode === 'choose' && 'Choose how you want to unlock your account'}
            {mode === 'email' && 'Enter your admin email to receive an unlock link'}
            {mode === 'otp' && 'Enter your admin email to receive an OTP'}
            {mode === 'link-sent' && 'Check your email for the unlock link'}
            {mode === 'enter-otp' && 'Enter the OTP sent to your email'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Choose Method */}
          {mode === 'choose' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  placeholder="admin@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <Button
                className="w-full bg-gradient-to-r from-purple-600 to-violet-600"
                onClick={() => setMode('email')}
                disabled={!email}
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Unlock Link via Email
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Or</span>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => setMode('otp')}
                disabled={!email}
              >
                <Key className="w-4 h-4 mr-2" />
                Login with OTP
              </Button>

              <Button
                variant="ghost"
                className="w-full"
                onClick={() => navigate('/admin/login')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </div>
          )}

          {/* Email Link Mode */}
          {mode === 'email' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  placeholder="admin@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <Button
                className="w-full bg-gradient-to-r from-purple-600 to-violet-600"
                onClick={requestUnlockLink}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Send Unlock Link
              </Button>

              <Button variant="ghost" className="w-full" onClick={() => setMode('choose')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          )}

          {/* Link Sent Confirmation */}
          {mode === 'link-sent' && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-slate-600">
                We've sent an unlock link to <strong>{email}</strong>. 
                Click the link in the email to unlock your account.
              </p>
              <p className="text-sm text-slate-500">
                The link expires in 15 minutes.
              </p>
              <Button variant="outline" className="w-full" onClick={() => setMode('choose')}>
                Didn't receive it? Try again
              </Button>
            </div>
          )}

          {/* OTP Mode */}
          {mode === 'otp' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  placeholder="admin@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <Button
                className="w-full bg-gradient-to-r from-purple-600 to-violet-600"
                onClick={requestOtp}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Key className="w-4 h-4 mr-2" />
                )}
                Send OTP
              </Button>

              <Button variant="ghost" className="w-full" onClick={() => setMode('choose')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          )}

          {/* Enter OTP */}
          {mode === 'enter-otp' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Enter 6-digit OTP</Label>
                <Input
                  type="text"
                  placeholder="123456"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest"
                  maxLength={6}
                />
                <p className="text-xs text-slate-500 text-center">
                  OTP sent to {email}. Valid for 10 minutes.
                </p>
              </div>

              <Button
                className="w-full bg-gradient-to-r from-purple-600 to-violet-600"
                onClick={verifyOtpAndLogin}
                disabled={loading || otp.length !== 6}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Unlock className="w-4 h-4 mr-2" />
                )}
                Unlock & Login
              </Button>

              <Button variant="ghost" className="w-full" onClick={requestOtp} disabled={loading}>
                Resend OTP
              </Button>

              <Button variant="ghost" className="w-full" onClick={() => setMode('choose')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUnlock;
